import { useRef } from "react";
import { useInView } from "framer-motion";
import CountUp from "react-countup";

// Extracted from HomePage.jsx's "By the Numbers" stats grid so it can be
// reused on other pages (e.g. CorporatePartnersPage) without duplicating it.
//
// `highlight` is optional and defaults to off — passing it adds a soft,
// blurred gold glow behind the card to draw the eye to one specific stat.
// It reuses the same bg-gold/10 decorative-circle treatment already used
// elsewhere on the site (e.g. HomePage's "Who We Serve" cards), just with
// blur added, rather than introducing a new visual effect.
// Mirrors CountUp's own thousands-separator formatting so the pre-animation
// fallback below is visually identical to the animation's final frame.
function formatStaticValue(value, separator) {
  if (!separator) return String(value);
  return value.toLocaleString("en-US").replace(/,/g, separator);
}

const StatCounter = ({ value, suffix, label, icon: Icon, highlight = false, separator = "," }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div ref={ref} className="stat-card relative bg-white border border-slate-100 p-8 group">
      {highlight && (
        <div
          className="absolute inset-0 -z-10 bg-gold/25 rounded-full blur-3xl scale-90"
          aria-hidden="true"
        />
      )}
      <div className="flex items-start justify-between mb-4">
        <Icon className="text-gold w-8 h-8" strokeWidth={1.5} />
        <span className="text-xs text-slate-400 uppercase tracking-widest">{label}</span>
      </div>
      <div className="stat-number font-outfit text-4xl md:text-5xl font-bold text-navy">
        {isInView ? (
          <CountUp end={value} duration={2.5} separator={separator} />
        ) : (
          formatStaticValue(value, separator)
        )}
        <span className="text-gold">{suffix}</span>
      </div>
    </div>
  );
};

export default StatCounter;
